import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, message, Tabs, Divider, Select, Row, Col } from 'antd';
import { UserOutlined, MedicineBoxOutlined, ShopOutlined, TeamOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import sellerService, { SellerRegistrationData } from '../services/sellerService';
import branchService, { BranchRegistrationData } from '../services/branchService';
import branchManagerService, { BranchManagerRegistrationData } from '../services/branchManagerService';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface SellerForm {
  seller_name: string;
  contact_person: string;
  email: string;
  phone: string;
  address?: string;
  username?: string;
  password: string;
}

interface BranchForm {
  branch_name: string;
  address: string;
  phone: string;
  branch_type: string;
  tax_id?: string;
  license_number?: string;
  // Branch Manager fields
  manager_name: string;
  manager_password: string;
  manager_email: string;
}

const Registration: React.FC = () => {
  const [loading, setLoading] = useState(false);
  // const [activeTab, setActiveTab] = useState('seller'); // Commented out seller registration
  const [activeTab, setActiveTab] = useState('branch'); // Set to 'branch' by default
  const navigate = useNavigate();

  // Helper function to generate username from manager name
  const generateUsername = (managerName: string): string => {
    return managerName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .trim();
  };

  // Commented out seller registration handler
  /*
  const handleSellerRegistration = async (values: SellerForm) => {
    setLoading(true);
    try {
      console.log('Attempting seller registration with:', values);
      
      const registrationData: SellerRegistrationData = {
        seller_name: values.seller_name,
        contact_person: values.contact_person,
        email: values.email,
        phone: values.phone,
        address: values.address || '',
        username: values.username || '',
        password: values.password,
        is_active: true
      };
      
      // Call the seller registration service
      const response = await sellerService.registerSeller(registrationData);
      
      console.log('Registration response:', response);
      
      // Handle successful registration response
      if (response.status === 'success') {
        // Auto-login the seller
        if (response.token) {
          localStorage.setItem('token', response.token);
          localStorage.setItem('user', JSON.stringify({
            id: response.data.id,
            username: response.data.attributes.username,
            email: response.data.attributes.email,
            seller_name: response.data.attributes.seller_name,
            contact_person: response.data.attributes.contact_person,
            is_main_seller: true
          }));
          
          message.success('Seller registration successful! You are now logged in.');
          navigate('/main/');
        } else {
          message.success('Seller registration successful! You can now login.');
          navigate('/login');
        }
      } else {
        message.error(response.message || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || error.response.data?.error || 'Registration failed';
        message.error(errorMessage);
      } else if (error.request) {
        // Network error
        message.error('Network error. Please check your connection and try again.');
      } else {
        // Other error
        message.error('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  */

  const handleBranchRegistration = async (values: BranchForm) => {
    setLoading(true);
    try {
      console.log('Attempting branch registration with:', values);
      
      // Step 1: Create the branch
      const branchData: BranchRegistrationData = {
        branch_name: values.branch_name,
        address: values.address,
        phone: values.phone,
        email: values.manager_email, // Use manager email as branch email
        branch_type: values.branch_type || 'retail',
        tax_id: values.tax_id || '',
        license_number: values.license_number || '',
        is_active: true
      };
      
      console.log('Creating branch...');
      const branchResponse = await branchService.registerBranch(branchData);
      
      if (branchResponse.status !== 'success') {
        throw new Error(branchResponse.message || 'Failed to create branch');
      }
      
      console.log('Branch created successfully:', branchResponse);
      
      // Step 2: Create the branch manager
      const generatedUsername = generateUsername(values.manager_name);
      console.log('Generated username:', generatedUsername);
      
      const managerData: BranchManagerRegistrationData = {
        branch_id: branchResponse.data.id,
        username: generatedUsername,
        password: values.manager_password,
        manager_name: values.manager_name,
        email: values.manager_email,
        phone: values.phone, // Use the same phone as branch
        is_active: true
      };
      
      console.log('Creating branch manager...');
      const managerResponse = await branchManagerService.registerBranchManager(managerData);
      
      if (managerResponse.status !== 'success') {
        // If manager creation fails, we should ideally rollback the branch creation
        // For now, we'll just show an error
        message.error('Branch created but manager creation failed. Please contact support.');
        return;
      }
      
      console.log('Branch manager created successfully:', managerResponse);
      
      // Step 3: Link branch and manager using update APIs
      try {
        console.log('Linking branch and manager...');
        
        // Update branch to link with manager
        const branchUpdateData = {
          branch_managers: [managerResponse.data.id]
        };
        
        // Update manager to link with branch (though it was already set during creation)
        const managerUpdateData = {
          branch: branchResponse.data.id
        };
        
        // Call both update APIs in parallel
        const [branchUpdateResponse, managerUpdateResponse] = await Promise.all([
          branchService.updateBranch(branchResponse.data.id, branchUpdateData),
          branchManagerService.updateBranchManager(managerResponse.data.id, managerUpdateData)
        ]);
        
        console.log('Branch update response:', branchUpdateResponse);
        console.log('Manager update response:', managerUpdateResponse);
        console.log('Branch and manager linked successfully');
        
        // Auto-login the branch manager
        if (managerResponse.token) {
          localStorage.setItem('token', managerResponse.token);
          localStorage.setItem('user', JSON.stringify({
            id: managerResponse.data.id,
            username: managerResponse.data.attributes.username,
            email: managerResponse.data.attributes.email,
            manager_name: managerResponse.data.attributes.manager_name,
            branch: branchResponse.data.id,
            is_branch_manager: true
          }));
          
          message.success('Branch and manager registration successful! You are now logged in.');
          navigate('/branch');
        } else {
          message.success('Branch and manager registration successful! You can now login with the manager credentials.');
          navigate('/login');
        }
        
      } catch (linkError: any) {
        console.error('Error linking branch and manager:', linkError);
        message.warning('Branch and manager created successfully, but linking failed. Please contact support to complete the setup.');
        navigate('/login');
      }
      
    } catch (error: any) {
      console.error('Registration error:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || error.response.data?.error || 'Registration failed';
        message.error(errorMessage);
      } else if (error.request) {
        // Network error
        message.error('Network error. Please check your connection and try again.');
      } else {
        // Other error
        message.error('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 600,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          borderRadius: '12px'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <MedicineBoxOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Medicine CRM Registration
          </Title>
          <Text type="secondary">
            Choose your registration type
          </Text>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          centered
          items={[
            // Commented out seller registration tab - only branch registration is enabled
            /*
            {
              key: 'seller',
              label: (
                <span>
                  <MedicineBoxOutlined />
                  Seller Registration
                </span>
              ),
              children: (
                <Form
                  name="sellerRegistration"
                  onFinish={handleSellerRegistration}
                  layout="vertical"
                  size="large"
                  onFinishFailed={(errorInfo) => {
                    console.log('Form validation failed:', errorInfo);
                  }}
                >
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="seller_name"
                        label="Seller Name"
                        rules={[
                          { required: true, message: 'Please enter seller name' },
                          { min: 2, message: 'Seller name must be at least 2 characters' }
                        ]}
                      >
                        <Input
                          prefix={<MedicineBoxOutlined />}
                          placeholder="Enter seller name"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="contact_person"
                        label="Contact Person"
                        rules={[
                          { required: true, message: 'Please enter contact person name' },
                          { min: 2, message: 'Contact person name must be at least 2 characters' }
                        ]}
                      >
                        <Input
                          prefix={<UserOutlined />}
                          placeholder="Enter contact person name"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="email"
                        label="Email Address"
                        rules={[
                          { required: true, message: 'Please enter email address' },
                          { type: 'email', message: 'Please enter a valid email address' }
                        ]}
                      >
                        <Input
                          prefix={<MailOutlined />}
                          placeholder="Enter email address"
                          type="email"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="phone"
                        label="Phone Number"
                        rules={[
                          { required: true, message: 'Please enter phone number' },
                          { pattern: /^[0-9+\-\s()]+$/, message: 'Please enter a valid phone number' }
                        ]}
                      >
                        <Input
                          prefix={<PhoneOutlined />}
                          placeholder="Enter phone number"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="address"
                    label="Address (Optional)"
                  >
                    <TextArea
                      placeholder="Enter address"
                      rows={2}
                    />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="username"
                        label="Username (Optional)"
                      >
                        <Input placeholder="Enter username" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="password"
                        label="Password"
                        rules={[
                          { required: true, message: 'Please enter password' },
                          { min: 6, message: 'Password must be at least 6 characters' }
                        ]}
                      >
                        <Input.Password placeholder="Enter password" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      style={{ width: '100%', height: 48 }}
                    >
                      Register as Seller
                    </Button>
                  </Form.Item>
                </Form>
              )
            },
            */
            {
              key: 'branch',
              label: (
                <span>
                  <ShopOutlined />
                  Branch & Manager Registration
                </span>
              ),
              children: (
                <Form
                  name="branchRegistration"
                  onFinish={handleBranchRegistration}
                  layout="vertical"
                  size="large"
                  onFinishFailed={(errorInfo) => {
                    console.log('Form validation failed:', errorInfo);
                  }}
                >
                  <Divider>
                    <ShopOutlined style={{ marginRight: 8 }} />
                    Branch Details
                  </Divider>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="branch_name"
                        label="Branch Name"
                        rules={[
                          { required: true, message: 'Please enter the branch name' },
                          { min: 2, message: 'Branch name must be at least 2 characters' }
                        ]}
                      >
                        <Input
                          prefix={<ShopOutlined />}
                          placeholder="Enter branch name"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="branch_type"
                        label="Branch Type"
                        rules={[{ required: true, message: 'Please select branch type' }]}
                      >
                        <Select placeholder="Select branch type">
                          <Option value="retail">Retail</Option>
                          <Option value="wholesale">Wholesale</Option>
                          <Option value="warehouse">Warehouse</Option>
                          <Option value="distribution">Distribution</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="address"
                    label="Address"
                    rules={[
                      { required: true, message: 'Please enter the branch address' },
                      { min: 10, message: 'Address must be at least 10 characters' }
                    ]}
                  >
                    <TextArea
                      placeholder="Enter complete branch address"
                      rows={3}
                    />
                  </Form.Item>

                  <Form.Item
                    name="phone"
                    label="Phone"
                    rules={[
                      { required: true, message: 'Please enter phone number' },
                      { 
                        pattern: /^\d{10}$/, 
                        message: 'Please enter exactly 10 digits (numbers only)' 
                      }
                    ]}
                  >
                    <Input
                      prefix={<PhoneOutlined />}
                      placeholder="Enter 10 digit phone number (e.g., 9876543210)"
                      maxLength={10}
                      onKeyPress={(e) => {
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="tax_id"
                        label="Tax ID (Optional)"
                      >
                        <Input placeholder="Enter tax ID" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="license_number"
                        label="License Number (Optional)"
                      >
                        <Input placeholder="Enter license number" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider>
                    <TeamOutlined style={{ marginRight: 8 }} />
                    Branch Manager Details
                  </Divider>

                  <Form.Item
                    name="manager_name"
                    label="Manager Name"
                    rules={[
                      { required: true, message: 'Please enter the manager name' },
                      { min: 2, message: 'Manager name must be at least 2 characters' }
                    ]}
                  >
                    <Input
                      prefix={<UserOutlined />}
                      placeholder="Enter manager name"
                    />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="manager_password"
                        label="Password"
                        rules={[
                          { required: true, message: 'Please enter password' },
                          { min: 6, message: 'Password must be at least 6 characters' }
                        ]}
                      >
                        <Input.Password placeholder="Enter password" autoComplete="new-password" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="manager_email"
                        label="Manager Email"
                        rules={[
                          { required: true, message: 'Please enter manager email' },
                          { type: 'email', message: 'Please enter a valid email address' }
                        ]}
                      >
                        <Input
                          prefix={<MailOutlined />}
                          placeholder="Enter manager email"
                          type="email"
                          autoComplete="off"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      style={{ width: '100%', height: 48 }}
                    >
                      Register Branch & Manager
                    </Button>
                  </Form.Item>
                </Form>
              )
            }
          ]}
        />

        <Divider />

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Typography.Text type="secondary">
            Already have an account?{' '}
            <Typography.Link onClick={() => navigate('/login')}>
              Login here
            </Typography.Link>
          </Typography.Text>
        </div>
      </Card>
    </div>
  );
};

export default Registration;
